import React from "react";
import { ShieldAlert, ExternalLink } from "lucide-react";
import { SUPPORT_EMAIL } from "./SupportCard";

/**
 * Shown when the login/register POST succeeds (cookies were sent in
 * Set-Cookie) but the very next /api/auth/me request returns 401 — meaning
 * the browser dropped the cookie. The usual cause is Safari ITP / Brave /
 * Chrome strict-tracking-prevention treating our auth cookie as third-party.
 *
 * The user-facing copy gives a clear, non-technical fix path.
 */
export default function CookieWarningBanner({ onClose }) {
  return (
    <div
      className="card-surface p-4 sm:p-5 border-[var(--bear)]/30 relative overflow-hidden"
      data-testid="cookie-warning-banner"
      role="alert"
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-60"
        style={{ background: "radial-gradient(circle at 100% 0%, rgba(255,71,87,0.18), transparent 55%)" }}
      />
      <div className="relative flex gap-3">
        <div
          className="w-9 h-9 rounded-xl bg-white/[0.04] border border-[var(--bear)]/40 flex items-center justify-center shrink-0"
          style={{ boxShadow: "0 0 24px rgba(255,71,87,0.22)" }}
        >
          <ShieldAlert className="w-4 h-4 text-[var(--bear)]" />
        </div>
        <div className="min-w-0">
          <div className="text-white font-semibold tracking-tight text-[14px]">
            Your browser is blocking our login cookie
          </div>
          <p className="text-white/65 text-[13px] leading-relaxed mt-1.5">
            You signed in correctly, but your browser is in strict tracking-prevention mode
            and dropped the FlowFinder session cookie. Two quick fixes:
          </p>
          <ul className="text-white/70 text-[13px] leading-relaxed mt-2 space-y-1 list-disc pl-4">
            <li>
              <span className="text-white">Safari</span> · Settings → Privacy → uncheck
              "Prevent cross-site tracking", then reload.
            </li>
            <li>
              <span className="text-white">Brave / Chrome strict mode</span> · Allow cookies
              for <span className="font-mono text-[12px] text-white">flowfinder.us</span>
              {" "}in the site shield / cookies panel, then reload.
            </li>
            <li>
              <span className="text-white">Incognito / Private window</span> · Most private
              modes block third-party cookies by default — try a normal window.
            </li>
          </ul>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              onClick={() => window.location.reload()}
              className="btn-accent text-xs py-1.5 px-3"
              data-testid="cookie-warning-retry"
            >
              Reload &amp; try again
            </button>
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=FlowFinder%20login%20cookie%20blocked&body=Hi%20FlowFinder%20team%2C%20my%20browser%20is%20blocking%20the%20login%20cookie.%0A%0ABrowser%3A%20%0AOS%3A%20%0AURL%3A%20%0A`}
              className="btn-ghost text-xs py-1.5 px-3 inline-flex items-center gap-1.5"
              data-testid="cookie-warning-contact"
            >
              <ExternalLink className="w-3 h-3" />
              Contact support
            </a>
            {onClose && (
              <button
                onClick={onClose}
                className="text-white/40 hover:text-white text-[11px] tracking-widest uppercase font-mono ml-1"
                data-testid="cookie-warning-dismiss"
              >
                Dismiss
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
